import React from "react";
import { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import Lottie from "react-lottie-player";
import lottieLocation from "../lotties/location-lottie.json";
import lottieClock from "../lotties/clock-lottie.json";
import lottieGlobe from "../lotties/globe-lang-lottie.json";
import lottieGrad from "../lotties/graduate-lottie.json";
import lottieExperience from "../lotties/experience.json";
import { SocialLinks } from "./SocialLinks";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { AuroraBackground } from "../components/Aurora";
import { TextHoverEffect } from "../components/TextHover";

export const BentoGrid = ({
  showAbout,
  setShowAbout,
  setShowSkills,
  setShowWork,
}) => {
  const app = useRef(null);
  const ServicePill = ({ serviceName, serviceLogo }) => {
    return (
      <div className="flex items-center gap-2 border-[0.5px] border-white px-4 py-2 rounded-2xl mr-16 text-[18px] font-thin">
        <img src={`/images/${serviceLogo}`} className="w-12"></img>
        {serviceName}
      </div>
    );
  };

  const LottieHolder = ({ lottie, text }) => {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-transparent border-[0.5px] border-white rounded-full">
        <div className="bg-white rounded-full">
          <Lottie
            loop
            animationData={lottie}
            play
            direction={1}
            useSubframes
            style={{ width: 30, height: 30 }}
          />
        </div>
        {text}
      </div>
    );
  };

  const InfoPills = ({ lottie, desc, title }) => {
    return (
      <div className="flex flex-col items-center justify-center gap-0 p-4 bg-gray-800 bg-opacity-80 border border-white rounded-2xl">
        <p className="text-6xl font-extrabold bg-gradient-to-b from-white to-gray-800 bg-clip-text text-transparent leading-none">
          {title}
        </p>
        <div className="flex gap-2 items-center justify-center px-3 py-2 rounded-full bg-white w-[120px] text-black">
          <div className="">
            <Lottie
              loop
              animationData={lottie}
              play
              style={{ width: 30, height: 30 }}
            />
          </div>
          {desc}
        </div>
      </div>
    );
  };

  // const doRecursion = (n) => {
  //   if(n===0) {
  //     console.log("TASK COMPLETED");
  //     return
  //   }
  //   console.log("RECURSIOM HAPPENING");
  //   doRecursion(n-1);
  // }

  // doRecursion(1000);

  useLayoutEffect(() => {
    const aboutAnimations = gsap.context(() => {
      gsap.to(".panel, .close-button", { display: "block" });
      showAbout === "show"
        ? gsap.fromTo(
            ".bento-section",
            {
              skewY: -10,
              skewX: 40,
              x: 1000,
              opacity: 0,
              // scrollTrigger: {
              //   trigger: ".bento-grid-main",
              //   start: "top 100%",
              //   end: "bottom 50%+=100px",
              // },
            },
            {
              skewY: 0,
              opacity: 1,
              skewX: 0,
              x: 0,
              stagger: 0.05,
              duration: 0.8,
              ease: "back.out",
            }
          )
        : gsap.fromTo(
            ".bento-section",
            {
              skewY: 0,
              skewX: 0,
              opacity: 1,
              x: 0,
              // scrollTrigger: {
              //   trigger: ".bento-grid-main",
              //   start: "top 100%",
              //   end: "bottom 50%+=100px",
              // },
              onComplete: () => {
                gsap.to(".panel, .close-button", { display: "none" });
              },
            },
            {
              skewY: 10,
              skewX: 40,
              opacity: 0,
              x: 1000,
              stagger: -0.1,
              duration: 0.6,
              ease: "back.out",
            }
          );
    });
    return () => aboutAnimations.revert();
  }, [showAbout]);

  const BentoSection = ({ children, className = "" }) => (
    <motion.div
      className={`bento-section backdrop-blur-3xl border-[0.5px] border-white rounded-2xl ${className}`}
      whileHover={{ scale: 1.02 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      {children}
    </motion.div>
  );

  return (
    <>
      {showAbout !== "" && (
        <>
          <div
            onClick={() => {
              setShowAbout("hide");
              setShowWork("hide");
            }}
            className="absolute close-button lg:top-10 lg:right-10 top-8 right-6 lg:p-6 p-2 border-[0.5px] border-white leading-none bg-black z-30 font-raleway text-2xl font-thin text-white cursor-pointer rounded-lg hover:scale-[1.1] hover:bg-white duration-500 hover:text-black transition-all"
          >
            X
          </div>

          <div
            className={`absolute top-0 left-0 panel gap-12 red w-full h-full flex-horizontalScroll font-[raleway] px-20 py-8 lg:px-0 lg:py-0 z-20 overflow-scroll lg:overflow-hidden backdrop-blur-lg`}
          >
            {/* <div className="flex flex-col gap-8 relative w-full rounded-3xl p-16"> */}
            <div className="flex flex-col gap-8 2xl:p-[140px] xl:p-[100px] lg:grid lg:grid-cols-8 grid-cols-1 grid-rows-1 gap-x-[40px] gap-y-[40px] lg:grid-rows-3 w-full h-full bento-grid-main z-10 ">
              <div className="lg:row-span-2 lg:col-start-1 lg:col-end-5 border-[0.5px] border-white rounded-0 bento-section rounded-lg backdrop-blur-3xl bg-black bg-opacity-40">
                <div className="flex flex-col items-start justify-start w-full h-full py-10 px-6 transition-all duration-[0.8s]  font-thin">
                  <div className="flex lg:flex-row flex-col gap-6">
                    <img src="/images/cartoon_2.jpeg" className="bg-black brightness-75 object-contain lg:w-1/3 h-[200px] border-[0.5px] border-white rounded-xl" />
                    <div className="flex flex-col gap-8 lg:pl-8 lg:w-2/3">
                      <div className="flex items-center gap-2 px-4 border-[0.5px] border-white rounded-2xl">
                        <p className="text-green-600 animate-pulse text-[50px] !leading-none">
                        •
                        </p>
                        Available to Work
                      </div>
                      <p className="text-[12px] lg:text-[16px] font-[raleway]">
                      Hey, I'm a frontend developer passionate about crafting intuitive user experiences. I specialize in creating dynamic interfaces using Three.js, React Three Fiber, Next.js, GSAP, Framer Motion and Tailwind CSS. 
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-4 pt-6">
                    <LottieHolder
                      lottie={lottieLocation}
                      text={"Mumbai, India"}
                    />
                    <LottieHolder
                      lottie={lottieGrad}
                      text={"Front-End Developer"}
                    />
                    <LottieHolder
                      lottie={lottieGlobe}
                      text={"English, Hindi, Tamil"}
                    />
                    <LottieHolder lottie={lottieClock} text={"IST"} />
                  </div>
                </div>
              </div>
              {/* <div className="flex gap-32 items-end "> */}
              <div className="lg:col-start-5 lg:col-end-9 gap-8 border-[0.5px] border-white rounded-0 bento-section rounded-lg backdrop-blur-3xl bg-black bg-opacity-40">
                <div className="flex flex-col items-center justify-center gap-4 px- lg:py-2 py-8 transition-all duration-[0.8s] w-full h-full">
                  {/* <TextHoverEffect className="text-2xl" text="SERVICES" /> */}
                  <p className="w-full text-center text-[36px] lg:text-[48px] font-thin">
                    Services
                  </p>
                  {/* </TextHoverEffect> */}
                  <Marquee
                    direction="right"
                    gradient
                    gradientColor="black"
                    gradientWidth={20}
                  >
                    <ServicePill
                      serviceLogo={"framer_logo.svg"}
                      serviceName={"Framer Development"}
                    />
                    <ServicePill
                      serviceLogo={"react_logo.svg"}
                      serviceName={"Front end development"}
                    />
                    <ServicePill
                      serviceLogo={"webflow_logo.svg"}
                      serviceName={"Webflow development"}
                    />
                    <ServicePill
                      serviceLogo={"three_logo.svg"}
                      serviceName={"3D development"}
                    />
                    <ServicePill
                      serviceLogo={"seo.svg"}
                      serviceName={"SEO Optimisation"}
                    />
                  </Marquee>
                </div>
              </div>
              <div className="col-start-5 col-end-7 border-[0.5px] border-white rounded-0 bento-section backdrop-blur-3xl rounded-lg gap-4 bg-black bg-opacity-40">
              {/* <BentoSection className="col-start-5 col-end-7"> */}
                <div className="flex flex-col items-center justify-center w-full h-full text-center transition-all  duration-[0.8s] py-4 lg:py-2 font-thin">
                  {/* <SocialLinks /> */}
                  {/* <p>Building Bridges Between Code and Creativity</p> */}
                  <p>
                    <span className="text-[32px] lg:text-[56px]">3+ yrs</span>{" "}
                    <br /> Work Experience
                  </p>
                </div>
              {/* </BentoSection> */}
              </div>

              <div className="col-start-7 col-end-9  border-[0.5px] border-white rounded-0 bento-section rounded-lg cursor-pointer  backdrop-blur-3xl bg-black bg-opacity-40">
                <div className="flex flex-col items-center justify-center text-center w-full h-full px-6 py-2 transition-all duration-[0.8s]">
                  <div className="flex items-center justify-center gap-[10px] w-full">
                    <img className="w-16" src="/images/linkedin.svg"></img>
                    <img className="w-16" src="/images/instagram.svg"></img>
                    <img className="w-16" src="/images/github.svg"></img>
                  </div>
                </div>
              </div>

              {/* </div> */}

              <div className="col-start-1 col-end-7 w-full border-[0.5px] border-white rounded-0 bento-section backdrop-blur-3xl bg-black bg-opacity-40 rounded-lg text-cente">
                <div className="flex md:flex-row flex-col items-center lg:gap-16 justify-center transition-all duration-[0.8s] px-6 py-2 w-full h-full">
                  <p className="text-[32px] lg:text-[32px] font-thin">
                    Work Experience
                  </p>
                  <div className="flex lg:flex-row flex-col items-center gap-8 w-full h-full py-8">
                    <div className="flex flex-col gap-4 border-[0.5px] border-white rounded-2xl lg:h-[140px] w-full px-6 py-4 font-thin">
                      <p className="text-[18px]">
                        Interactive Developer Intern <br /> @ InkInCaps (6
                        months) <br />
                      </p>
                      <p className="text-[12px] leading-6">
                        Worked on creating an interactive metaverse environment{" "}
                        where players could interact online via avatars
                      </p>
                    </div>
                    <div className="lg:flex hidden items-center justify-center">
                      {"►"}
                    </div>
                    <p className="flex lg:hidden">V</p>
                    <div className="flex flex-col gap-4 border-[0.5px] border-white rounded-2xl h-[140px] w-full px-6 py-4 font-thin">
                      <p className="text-[18px]">
                        Frontend Developer <br /> @ Designare.Studio (2 years){" "}
                        <br />
                      </p>
                      <p className="text-[12px] leading-6">
                        10+ projects completed so far
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-start-7 col-end-9 flex items-center justify-start border-[0.5px] border-white rounded-0 bento-section rounded-lg backdrop-blur-3xl bg-black bg-opacity-40">
                <div className="flex flex-col w-full h-full items-start gap-4 transition-all duration-[0.8s] px-6 py-2 font-thin ">
                  <p className="text-[24px]">What's next for me?</p>
                  <p className="text-[12px]">
                  Currently expanding into backend development with PostgreSQL to evolve into a versatile full-stack developer who can bring ambitious web projects to life from concept to completion.
                  </p>
                </div>
              </div>
            </div>
            {/* <AuroraBackground className="scale-x-[-1]"></AuroraBackground> */}
          </div>
        </>
      )}
    </>
  );
};